/**
 * Plan of Record — Strategic Priorities View
 *
 * Shows the accepted strategic priorities for the Plan of Record,
 * drawn from proposals accepted in the linked prioritization cycle.
 * Each priority shows: classification, owner, rationale, evidence,
 * lineage link, and relationship graph link.
 *
 * "The output is not a ranked list. The output is a durable Plan of Record
 *  with preserved context, decision lineage, visible relationships, and
 *  enough organizational memory for the decisions to hold under execution
 *  pressure."
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle2,
  GitBranch,
  Network,
  User,
  BookOpen,
  AlertCircle,
  Loader2,
  ArrowUpRight,
} from 'lucide-react';
import type { AssembledPor } from '../../types/plan-of-record';
import { useProposalPacketStore } from '../../stores/prioritization/proposalPacketStore';
import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { CategoryChip, AltitudeChip } from '../../components/prioritization/ItemClassificationBadge';
import type { CatchballProposal } from '../../types/prioritization/catchballProposal';
import type { Item } from '../../types/prioritization/lifecycle';
import { fetchHeldValues } from '../../lib/itemCycleStandingService';
import { itemAsHeld, itemInContextHref, type ItemHeldValues } from '../../lib/itemCycleStanding';
import { PorNarrativeMarkdown } from '../../components/plan-of-record/PorNarrativeMarkdown';

// ── Priority card ─────────────────────────────────────────────────────────────

interface PriorityEntry {
  rank: number;
  title: string;
  rationale: string;
  evidence: string[];
  item: Item | null;
  proposedByOrgUnit: string;
  proposal: CatchballProposal;
}

function PriorityCard({ entry, planId }: { entry: PriorityEntry; planId: string | null | undefined }) {
  const [expanded, setExpanded] = useState(false);
  const { item, rank, title, rationale, evidence, proposedByOrgUnit } = entry;

  return (
    <div className="border border-[var(--rule)] bg-[var(--bg-raised)] overflow-hidden">
      {/* Header */}
      <div
        className="px-6 py-5 flex items-start gap-4 cursor-pointer hover:bg-[var(--surface)] transition-colors"
        onClick={() => setExpanded((v) => !v)}
      >
        {/* Rank badge */}
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--slate-dim)] text-sm font-bold text-[var(--text-sec)]">
          {rank}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-base font-semibold text-[var(--text-pri)]">{title}</h3>
              {/* Classification */}
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                {item?.itemCategory && <CategoryChip category={item.itemCategory} />}
                {item?.altitudeLevel && <AltitudeChip level={Number(item.altitudeLevel)} category={item.itemCategory} />}
                <span className="inline-flex items-center gap-1 text-xs text-[var(--text-ter)]">
                  <User className="h-3 w-3" />
                  {proposedByOrgUnit}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="inline-flex items-center gap-1 rounded-full bg-[var(--green-dim)] px-2.5 py-0.5 text-xs font-medium text-[var(--green)] dark:text-[var(--text-ter)]">
                <CheckCircle2 className="h-3 w-3" />
                Accepted
              </span>
            </div>
          </div>

          {/* Rationale preview */}
          {rationale && !expanded && (
            <p className="mt-2 text-sm text-[var(--text-sec)] line-clamp-2">{rationale}</p>
          )}
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t border-[var(--rule)] divide-y divide-[var(--rule)]">
          {rationale && (
            <div className="px-6 py-4">
              <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide mb-2 dark:text-[var(--text-ter)]">
                <BookOpen className="inline h-3.5 w-3.5 mr-1" />
                Rationale
              </p>
              <PorNarrativeMarkdown source={rationale} />
            </div>
          )}

          {evidence.length > 0 && (
            <div className="px-6 py-4">
              <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide mb-2 dark:text-[var(--text-ter)]">
                Supporting evidence
              </p>
              <ul className="space-y-2">
                {evidence.map((e, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                    <span className="mt-1.5 h-1 w-1 rounded-full bg-[var(--text-ter)] shrink-0" />
                    {e}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Item links */}
          {item && (
            <div className="px-6 py-4 bg-[var(--surface)]">
              <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide mb-3">Decision lineage</p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to={`/prioritization/items/${item.id}/journey`}
                  className="inline-flex items-center gap-1.5 border border-[var(--rule)] bg-[var(--bg-raised)] px-3 py-2 text-sm text-[var(--text-sec)] hover:border-[var(--slate)] hover:text-[var(--text-ter)] transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  <GitBranch className="h-4 w-4" />
                  Item lineage
                  <ArrowUpRight className="h-3.5 w-3.5 opacity-60" />
                </Link>
                <Link
                  to={itemInContextHref(item.id, { tab: 'relationships', planCycleId: planId })}
                  className="inline-flex items-center gap-1.5 border border-[var(--rule)] bg-[var(--bg-raised)] px-3 py-2 text-sm text-[var(--text-sec)] hover:border-[var(--slate)] hover:text-[var(--text-ter)] transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Network className="h-4 w-4" />
                  Relationship graph
                  <ArrowUpRight className="h-3.5 w-3.5 opacity-60" />
                </Link>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function PorStrategicPriorities({ assembled }: { assembled: AssembledPor }) {
  const cycleId = assembled.record.plan_cycle_id;
  const proposals = useProposalPacketStore((s) => s.proposals);
  const fetchProposals = useProposalPacketStore((s) => s.fetchProposals);
  const isLoading = useProposalPacketStore((s) => s.isLoading);
  const allItems = useItemsStore((s) => s.items);
  const fetchItems = useItemsStore((s) => s.fetchFromSupabase);

  useEffect(() => {
    if (cycleId) fetchProposals(cycleId);
    fetchItems();
  }, [cycleId, fetchProposals, fetchItems]);

  // A plan shows the version it authorized, not the live item (migration 1019).
  const [heldByItem, setHeldByItem] = useState<Map<string, ItemHeldValues>>(new Map());
  const itemIdKey = allItems.map((i) => i.id).sort().join(',');
  useEffect(() => {
    if (!cycleId || !itemIdKey) return;
    let live = true;
    void fetchHeldValues(itemIdKey.split(','), cycleId).then((held) => live && held && setHeldByItem(held));
    return () => {
      live = false;
    };
  }, [cycleId, itemIdKey]);
  const asHeld = (item: Item | null): Item | null => {
    const held = item ? heldByItem.get(item.id) : undefined;
    return item && held ? itemAsHeld(item, held) : item;
  };

  // Get accepted proposals for this cycle (includes accepted_provisional from the DB)
  const acceptedProposals = proposals.filter(
    (p) => p.cycleId === cycleId && (p.status === 'accepted' || p.status === 'accepted_provisional') && p.direction === 'up'
  );

  // Build priority entries from accepted proposals
  const priorities: PriorityEntry[] = [];
  let rank = 1;
  for (const proposal of acceptedProposals) {
    const items = proposal.readinessSnapshot?.items ?? [];
    if (items.length > 0) {
      items.forEach((ri) => {
        const item = asHeld(allItems.find((it) => it.id === ri.itemId) ?? null);
        priorities.push({
          rank: rank++,
          // What the plan holds comes first (1060), then the title the proposal carried.
          title: (item && heldByItem.has(item.id) ? item.title : null) ?? ri.itemTitle ?? item?.title ?? 'Untitled',
          rationale: proposal.rationale ?? '',
          evidence: proposal.supportingEvidence ?? [],
          item,
          proposedByOrgUnit: proposal.proposedByOrgUnitName ?? proposal.proposedByName ?? 'Unknown',
          proposal,
        });
      });
    } else {
      // Proposal without items list — show as a single entry
      priorities.push({
        rank: rank++,
        title: proposal.itemTitle ?? `Proposal from ${proposal.proposedByOrgUnitName ?? 'Unknown'}`,
        rationale: proposal.rationale ?? '',
        evidence: proposal.supportingEvidence ?? [],
        item: proposal.itemId ? asHeld(allItems.find((it) => it.id === proposal.itemId) ?? null) : null,
        proposedByOrgUnit: proposal.proposedByOrgUnitName ?? proposal.proposedByName ?? 'Unknown',
        proposal,
      });
    }
  }

  // Also include commitments from the PoR as priorities if no cycle proposals exist
  const commitmentPriorities = assembled.commitments.map((c, i) => ({
    rank: priorities.length > 0 ? priorities.length + i + 1 : i + 1,
    title: c.title,
    rationale: c.description ?? '',
    evidence: [],
    item: asHeld(allItems.find((it) => it.title === c.title) ?? null),
    proposedByOrgUnit: assembled.record.title,
    proposal: null as unknown as CatchballProposal,
  }));

  const allPriorities = priorities.length > 0 ? priorities : commitmentPriorities;

  return (
    <div className="space-y-6">
      {/* Narrative close */}
      <div className="border border-[var(--slate)] bg-gradient-to-br from-indigo-50 to-white p-6 dark:border-[var(--rule)] dark:bg-[var(--bg-raised)] dark:[background-image:none]">
        <div className="flex items-start gap-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--slate-dim)] dark:bg-primary-950/40">
            <CheckCircle2 className="h-5 w-5 text-[var(--text-ter)] dark:text-primary-400" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-[var(--text-pri)]">{assembled.record.title}</h2>
            <p className="mt-1 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] leading-relaxed max-w-2xl">
              The output is not a ranked list. It is a durable Plan of Record with preserved context,
              decision lineage, visible relationships, and enough organizational memory for the
              decisions to hold under execution pressure.
            </p>
            <div className="mt-3 flex flex-wrap gap-4 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
              <span>v{assembled.record.version}</span>
              <span className="capitalize">{assembled.record.status}</span>
              {assembled.record.owner_email && (
                <span className="flex items-center gap-1">
                  <User className="h-3 w-3" />
                  {assembled.record.owner_email}
                </span>
              )}
              {allPriorities.length > 0 && (
                <span>{allPriorities.length} accepted {allPriorities.length === 1 ? 'priority' : 'priorities'}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Loading */}
      {isLoading && cycleId && (
        <div className="flex items-center gap-2 text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading accepted proposals…
        </div>
      )}

      {/* No cycle linked */}
      {!cycleId && (
        <div className="border border-[var(--mustard)] bg-[var(--mustard-dim)] px-5 py-4 flex items-start gap-3 dark:border-[var(--mustard)]/50 dark:bg-[var(--mustard)]/25 dark:ring-1 dark:ring-amber-800/25">
          <AlertCircle className="h-4 w-4 text-[var(--mustard)] shrink-0 mt-0.5 dark:text-[var(--mustard)]" />
          <div>
            <p className="text-sm font-medium text-[var(--mustard)] dark:text-[var(--mustard)]">No cycle linked</p>
            <p className="text-xs text-[var(--mustard)] dark:text-[var(--mustard)]/90 mt-0.5">
              Link this Plan of Record to a prioritization cycle to show accepted strategic priorities.
              You can also view commitments entered in the Commitments section.
            </p>
          </div>
        </div>
      )}

      {/* No accepted priorities yet */}
      {!isLoading && cycleId && acceptedProposals.length === 0 && (
        <div className="border border-dashed border-[var(--rule)] bg-[var(--bg-raised)] p-10 text-center">
          <CheckCircle2 className="mx-auto h-10 w-10 text-[var(--text-ter)]" />
          <p className="mt-3 text-sm font-medium text-[var(--text-sec)]">No accepted priorities yet</p>
          <p className="mt-1 text-xs text-[var(--text-ter)]">
            Accept proposals in the Executive Reconciliation session to populate this view.
          </p>
        </div>
      )}

      {/* Priority list */}
      {allPriorities.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-[var(--text-pri)]">
              Accepted strategic priorities
            </h3>
            <span className="text-xs text-[var(--text-ter)]">
              {allPriorities.length} accepted · click any item to expand lineage
            </span>
          </div>
          {allPriorities.map((entry) => (
            <PriorityCard key={`${entry.rank}-${entry.title}`} entry={entry} planId={cycleId} />
          ))}
        </div>
      )}

      {/* Commitments as priorities when cycle exists but no proposals */}
      {!isLoading && cycleId && acceptedProposals.length === 0 && assembled.commitments.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-[var(--text-ter)]">Commitments (from PoR sections)</h3>
          {commitmentPriorities.map((entry) => (
            <PriorityCard key={`commitment-${entry.rank}`} entry={entry} planId={cycleId} />
          ))}
        </div>
      )}

      {/* Summary counts */}
      {allPriorities.length > 0 && (
        <div className="border border-[var(--rule)] bg-[var(--bg-raised)] p-5">
          <h3 className="text-sm font-semibold text-[var(--text-pri)] mb-4">Accepted priorities by type</h3>
          <div className="flex flex-wrap gap-2">
            {Array.from(
              new Set(allPriorities.map((p) => p.item?.itemCategory).filter(Boolean) as string[])
            ).map((cat) => {
              const count = allPriorities.filter((p) => p.item?.itemCategory === cat).length;
              return (
                <span key={cat} className="inline-flex items-center gap-1.5">
                  <CategoryChip category={cat} />
                  <span className="text-xs text-[var(--text-ter)]">×{count}</span>
                </span>
              );
            })}
            {allPriorities.every((p) => !p.item?.itemCategory) && (
              <p className="text-xs text-[var(--text-ter)]">Items have not been classified yet.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
